import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import styled, { css } from 'styled-components'

import useBlickBitesFastlaneContext from '@hooks/useBlickBitesFastlaneContext'
import useInViewChange from '@hooks/useInViewChange'
import {
  VideoMetadataPlaylist,
  VideoMetadataResponse,
} from '@hooks/useVideoMetadataFetching/types'
import {
  fetchVideoMetadata,
  getM3U8File,
  getSmallestJpgImage,
} from '@hooks/useVideoMetadataFetching/utils'
import { BlickBitesItem } from '@widgets/Video/BlickBites'

import BlickBitesFastlaneVideo from './BlickBitesFastlaneVideo'
import ItemContainer from './ItemContainer'

export interface BlickBitesFastlaneItemProps {
  item: BlickBitesItem
  onClose: () => void
  index: number
  activeIndex: number
  onImpression: (index: number, item: BlickBitesItem) => void
}

const StyledVideoPoster = styled.div<{ isVisible: boolean }>`
  ${({
    isVisible,
    theme: {
      colors: { staticNeutral950 },
    },
  }) => css`
    opacity: ${isVisible ? 1 : 0};
    height: 100%;
    width: 100%;
    position: absolute;
    background-color: ${staticNeutral950};
    z-index: 6;
    transition: opacity 0.5s ease-in-out;
  `}
`

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const BlickBitesFastlaneItem: FunctionComponent<
  BlickBitesFastlaneItemProps
> = ({ item, onClose, index, activeIndex, onImpression }) => {
  const { video, image } = item

  const videoId = video?.jwVideoId

  const [isInViewport, setIsInViewport] = useState<boolean | null>(null)
  const [videoMetadata, setVideoMetadata] = useState<
    VideoMetadataPlaylist | undefined
  >()

  const { isLoading, videoId: videoIdFromContext } =
    useBlickBitesFastlaneContext()

  const onInViewChangeHandler = useCallback((inView: any) => {
    setIsInViewport(inView)
  }, [])

  const viewportRef = useInViewChange({
    track: true,
    threshold: 0.5,
    delay: 0,
    onInViewChange: onInViewChangeHandler,
  })

  const isCurrentVideo = videoId === videoIdFromContext
  // hide video poster if it is current, is in viewport and is not loading
  const showVideoPoster = !(isInViewport && isCurrentVideo && !isLoading)
  // preload video if it is next or previous
  const shouldPreloadVideo =
    activeIndex + 1 === index || activeIndex - 1 === index

  useEffect(() => {
    if (isInViewport) {
      onImpression(index, item)
    }
  }, [index, isInViewport, item, onImpression])

  useEffect(() => {
    // fetch video metadata if it was not fetched yet and
    // the viewport is ready (value is not null) and
    // the video is in viewport or should be preloaded
    if (
      !videoMetadata &&
      isInViewport !== null &&
      (isInViewport || shouldPreloadVideo)
    ) {
      fetchVideoMetadata(videoId).then((res) => {
        const response = res as VideoMetadataResponse
        const playlist = response?.playlist?.[0]
        setVideoMetadata(playlist)

        if (shouldPreloadVideo) {
          const manifest = getM3U8File(playlist)

          if (manifest) {
            // preload video
            fetch(manifest)
          }
        }
      })
    }
  }, [isInViewport, shouldPreloadVideo, videoId, videoMetadata])

  return (
    <ItemContainer ref={viewportRef}>
      {isInViewport ? (
        <BlickBitesFastlaneVideo
          isCurrentVideo={isCurrentVideo}
          item={item}
          onClose={onClose}
        />
      ) : null}
      {videoMetadata ? (
        <StyledVideoPoster isVisible={showVideoPoster}>
          <StyledImage
            src={getSmallestJpgImage(videoMetadata)}
            alt={image.alt ?? ''}
          />
        </StyledVideoPoster>
      ) : null}
    </ItemContainer>
  )
}

export default BlickBitesFastlaneItem
